import { Component, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, ActivationEnd, Router } from '@angular/router';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { Breadcrumb } from './breadcrumb.model';
import { ROUTES } from '../../../shared/constants/routes';
import { SubscriptionVariablesService } from '../../../shared/services/subscription-variables.service';
import { uniqBy } from '../../../shared/utils/array/uniq-by';
import { isNotNil } from '../../../shared/utils/object/is-not-nil';

@Component({
  selector: 'app-breadcrumb',
  template: `
    <div class="container small-screen">
      @for (breadcrumb of breadcrumbs(); track breadcrumb; let i = $index; let isLast = $last; let isFirst = $first) {
        @let haveBreadcrumbLengthBiggerThanZero = breadcrumbs().length > 0;
        <div class="item" [class.is-first]="isFirst" [class.not-last]="!isLast && haveBreadcrumbLengthBiggerThanZero">
          @if (i > 0 && haveBreadcrumbLengthBiggerThanZero) {
            <mat-icon>chevron_right</mat-icon>
          }
          <a [class.hoverable]="!isLast && haveBreadcrumbLengthBiggerThanZero" [class.text-bold]="isLast" (click)="goOut(breadcrumb)">
            {{ breadcrumb.label }}
          </a>
        </div>
      }
    </div>
  `,
  imports: [MatIcon],
  styleUrls: ['./breadcrumb.component.scss'],
})
export class BreadcrumbComponent {
  private readonly subscriptionVariablesService = inject(SubscriptionVariablesService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  private readonly previousBreadcrumbs = signal<Breadcrumb[]>([]);

  private readonly routeChanged = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof ActivationEnd),
      map(() => this.router.url.split('?')[0]),
      distinctUntilChanged()
    )
  );

  private readonly breadcrumbUpdate = toSignal(this.subscriptionVariablesService.changeBreadcrumb);

  protected readonly breadcrumbs = signal<Breadcrumb[]>([]);

  constructor() {
    const homeBreadcrumb: Breadcrumb = {
      label: 'Home',
      url: '',
      key: 'home',
    };

    effect(() => {
      const routeUrl = this.routeChanged();
      if (routeUrl !== undefined) {
        untracked(() => {
          const current = this.breadcrumbs();
          if (current.length > 0) {
            this.previousBreadcrumbs.set([...current]);
          }

          const root: ActivatedRoute = this.route.root;
          const newBreadcrumbs = this.getBreadcrumbs(root);
          const uniqueBreadcrumbs = uniqBy([homeBreadcrumb, ...newBreadcrumbs], 'label');
          this.breadcrumbs.set(uniqueBreadcrumbs);

          if (this.subscriptionVariablesService.breadcrumbValues) {
            this.updateBreadcrumbLabel(this.subscriptionVariablesService.breadcrumbValues);
          }
        });
      }
    });

    effect(() => {
      const update = this.breadcrumbUpdate();
      if (update) {
        untracked(() => {
          this.updateBreadcrumbLabel(update);
        });
      }
    });
  }

  goOut(breadcrumb: Breadcrumb): void {
    if (breadcrumb.key === 'rcsa') {
      this.router.navigate(['/rcsas']);
      return;
    }

    if (breadcrumb.key === 'home') {
      this.router.navigate(['/']);
      return;
    }

    if (breadcrumb.key === 'standards') {
      this.router.navigate(['/standards']);
      return;
    }

    if (breadcrumb.key === 'homeEvents') {
      this.router.navigate(['/events']);
      return;
    }

    if (breadcrumb.key === 'homeIssues') {
      this.router.navigate(['/issues']);
      return;
    }

    if (breadcrumb.key === 'homeMyTodos') {
      this.router.navigate(['/todos']);
      return;
    }

    if (breadcrumb.key === 'homeLibraryControls') {
      this.router.navigate([`/${ROUTES.libraryControls}`]);
      return;
    }

    this.router.navigateByUrl(breadcrumb.url);
  }

  private updateBreadcrumbLabel(breadcrumb: Breadcrumb): void {
    this.breadcrumbs.update(current => {
      const updated = [...current];
      const obj = updated.find(o => o.key === breadcrumb.key);
      if (isNotNil(obj)) {
        obj.label = breadcrumb.label;
      }
      return updated;
    });
  }

  private getLogicalParentBreadcrumb(): Breadcrumb | undefined {
    const url = this.router.url;
    if (url.startsWith('/overview/event/')) {
      return {
        label: 'Events',
        url: '/events',
        key: 'homeEvents',
      };
    }
    if (url.startsWith('/overview/issue/')) {
      return {
        label: 'Issues',
        url: '/issues',
        key: 'homeIssues',
      };
    }
    if (url.startsWith(`/${ROUTES.libraryControl}`)) {
      return {
        label: 'Library Controls',
        url: `/${ROUTES.libraryControls}`,
        key: 'homeLibraryControls',
      };
    }
    return undefined;
  }

  private getBreadcrumbs(
    route: ActivatedRoute,
    url: string = '',
    breadcrumbs: Breadcrumb[] = [],
    previousBreadcrumb?: Breadcrumb
  ): Breadcrumb[] {
    const ROUTE_DATA_BREADCRUMB = 'breadcrumb';
    const children: ActivatedRoute[] = route.children;
    if (children?.length === 0) {
      return breadcrumbs;
    }
    const child = children[0];
    if (!child.snapshot.data.hasOwnProperty(ROUTE_DATA_BREADCRUMB)) {
      return this.getBreadcrumbs(child, url, breadcrumbs, previousBreadcrumb);
    }
    const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
    const newBreadcrumbData = child.snapshot.data[ROUTE_DATA_BREADCRUMB] as Breadcrumb;
    url += `/${routeURL}`;
    const breadcrumb: Breadcrumb = {
      label: newBreadcrumbData.label,
      url,
      key: newBreadcrumbData.key,
      shouldPreservePrevious: newBreadcrumbData.shouldPreservePrevious,
    };
    // If shouldPreservePrevious is true, inject last breadcrumb from previousBreadcrumbs (not home, not duplicate)
    if (breadcrumb.shouldPreservePrevious) {
      let injectedBreadcrumb: Breadcrumb | undefined;
      if (
        this.previousBreadcrumbs() &&
        this.previousBreadcrumbs().length > 1 // at least Home + one more
      ) {
        // Find last non-home breadcrumb from previous trail
        injectedBreadcrumb = [...this.previousBreadcrumbs()].reverse().find(b => b.key !== 'home' && b.key !== breadcrumb.key);
      } else {
        // Direct access or refresh: infer logical parent from URL
        injectedBreadcrumb = this.getLogicalParentBreadcrumb();
      }
      if (injectedBreadcrumb && !breadcrumbs.some(b => b.key === injectedBreadcrumb.key)) {
        breadcrumbs.push(injectedBreadcrumb);
      }
    }
    breadcrumbs.push(breadcrumb);
    return this.getBreadcrumbs(child, url, breadcrumbs, breadcrumb);
  }
}
